import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { Button, InputBar, Textarea } from '~/ui/index';
import UploadPhotoItem from '~/components/UploadPhoto/UploadPhotoItem/UploadPhotoItem';
import { type UploadPhotosValue } from '~/components/UploadPhoto/UploadPhoto';
import styles from './RequestForm.module.scss';
import { VideoPlayer } from '~/components/Shared/VideoPlayer/VideoPlayer';
import { useTranslation } from 'react-i18next';
import { getTimeDates } from '~/utils/getTimeDates';
import { SectionLink } from '~/ui/SectionLink/SectionLink';

export interface Photo {
  id: number;
  full_photo_url: string;
}

interface Video {
  id: number;
  full_video_url: string;
  full_preview_video_url: string;
}

export interface RequestFormItem {
  id: number;
  category_name: string;
  object_name: string;
  description: string;
  ticket_status: number;
  user_name: string;
  grade_name: string;
  created_at: string;
  user_uniqueId: string;
  ticket_photo: Photo[];
  ticket_video: Video[];
  ticket_review_photo: Photo[];
  ticket_review_video: Video[];
  business_trip: {
    accommodation: { name: string; section: string };
    room: { name: string };
  };
  working_period: string | null;
  user_id: number;
  review: string | null;
}

interface RequestFormProps {
  request: RequestFormItem;
  solution: string;
  setSolution: (solution: string) => void;
  updateTicketSolution: (params: {
    id: number;
    review: string;
    reviewImages: File[];
    reviewVideos: File[];
  }) => void;
  onReviewFilesChange?: (images: File[], videos: File[]) => void;
}

export const RequestForm = ({
  request,
  setSolution,
  updateTicketSolution,
  onReviewFilesChange,
}: RequestFormProps) => {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { t } = useTranslation();
  const videoInputRef = useRef<HTMLInputElement>(null);

  const photos = request?.ticket_photo;
  const name = request?.business_trip?.accommodation?.name || '';
  const room = request?.business_trip?.room?.name || '';
  const section = request?.business_trip?.accommodation?.section || '';
  const review = request?.review || '';

  const handleUpdate = () => {
    updateTicketSolution({
      id: request.id,
      review: formik.values.solution,
      reviewImages: formik.values.uploadReviewPhotos.files,
      reviewVideos: formik.values.reviewVideoFiles,
    });
  };

  const finallyInfo = `${name ? name + '/' : ''} ${
    section ? section + '/' : ''
  } ${room ? room : ''}`;

  const formik = useFormik({
    initialValues: {
      category: '',
      object_name: '',
      comments: '',
      status: '',
      user_name: '',
      grade: '',
      date: '',
      uniqueID: '',
      working_period: '',
      solution: '',
      uploadReviewPhotos: { photos: [], files: [] } as UploadPhotosValue,
      reviewVideoFiles: [] as File[],
    },

    onSubmit: () => {
      formik.resetForm();
    },
  });

  const videoFileUrls = useMemo(
    () =>
      formik.values.reviewVideoFiles.map(file => URL.createObjectURL(file)),
    [formik.values.reviewVideoFiles]
  );

  useEffect(() => {
    return () => {
      videoFileUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [videoFileUrls]);

  useEffect(() => {
    if (!request) return;

    formik.setValues(prev => ({
      ...prev,
      category: request.category_name,
      object_name: request.object_name,
      comments: request.description,
      status: request.ticket_status.toString(),
      user_name: request.user_name,
      grade: request.grade_name,
      date: getTimeDates(request.created_at),
      uniqueID: request.user_uniqueId,
      working_period: request.working_period || '',
      solution: request.review || '',
    }));
  }, [request]);

  return (
    <div className={styles.formWrapper}>
      <form
        className={styles.form}
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <div className={styles.formItems}>
          <div className={styles.inputWrapper}>
            <InputBar
              name="category"
              value={formik.values.category || t('not_specified')}
              disabled={true}
              readOnly={true}
              label={t('category')}
            />
            <InputBar
              name="working_period"
              value={formik.values.working_period || `(${t('empty')})`}
              disabled={true}
              readOnly={true}
              label={t('shift')}
            />
          </div>

          <InputBar
            name="object"
            value={formik.values.object_name || t('not_specified')}
            disabled={true}
            readOnly={true}
            label={t('object_place')}
          />

          <InputBar
            name="createdOn"
            value={
              request.business_trip && finallyInfo
                ? finallyInfo
                : t('not_specified')
            }
            disabled={true}
            readOnly={true}
            label={`${t('application_created_on')}:`}
          />

          <InputBar
            name="uniqueID"
            value={formik.values.uniqueID}
            disabled={true}
            readOnly={true}
            label={t('unique_user_ID')}
          />

          <div className={styles.infoWrapper}>
            <InputBar
              name="user_name"
              value={formik.values.user_name}
              disabled={true}
              readOnly={true}
              label={t('employee')}
              sectionLink={
                <SectionLink
                  title={t('to_contact')}
                  link={`/user/${request?.user_id}`}
                />
              }
            />

            <InputBar
              name="grade"
              value={formik.values.grade}
              disabled={true}
              readOnly={true}
              label={t('access_group')}
            />

            <InputBar
              name="date"
              value={formik.values.date}
              disabled={true}
              readOnly={true}
              label={t('date_and_time')}
            />
          </div>

          <div className={styles.descriptionContainer}>
            <span className={styles.solutionMainLabel}>{t('description')}</span>

            <Textarea
              showCounter
              rows={6}
              label=""
              placeholder={t('leave_comment')}
              maxLength={1000}
              value={formik.values.comments}
              onChange={formik.handleChange}
              name="comments"
              star={false}
              disabled={true}
              readOnly={true}
            />

            {(photos?.length > 0 || request?.ticket_video?.length > 0) && (
              <div className={styles.ticketMediaContainer}>
                <span className={styles.reviewSectionLabel}>Медиа</span>
                <div className={styles.mediaContainer}>
                  <div className={styles.imgContainer}>
                    {photos?.map(({ id, full_photo_url }) => (
                      <UploadPhotoItem
                        key={id}
                        url={full_photo_url}
                        deletePhoto={false}
                      />
                    ))}
                  </div>
                  {request.ticket_video.map(
                    ({ id, full_video_url, full_preview_video_url }) => (
                      <VideoPlayer
                        key={id}
                        url={full_video_url}
                        poster={full_preview_video_url}
                      />
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={styles.solutionContainer}>
            <span className={styles.solutionMainLabel}>{t('solution')}</span>

            <Textarea
              label=""
              name="solution"
              placeholder={t('solution_placeholder')}
              value={formik?.values?.solution}
              onChange={e => {
                formik.setFieldValue('solution', e.target.value);
                setSolution(e.target.value);
              }}
              showCounter
              rows={6}
              maxLength={1000}
              star={false}
            />

            <div className={styles.reviewMediaContainer}>
              <span className={styles.reviewSectionLabel}>
                Фото и видео к решению
              </span>

              <div className={styles.reviewMediaContent}>
              {request?.ticket_review_photo?.map(
                ({ id, full_photo_url }) => (
                  <UploadPhotoItem
                    key={id}
                    url={full_photo_url}
                    deletePhoto={false}
                  />
                )
              )}
              {formik.values.uploadReviewPhotos.files.map((file, index) => (
                <UploadPhotoItem
                  key={file.name + index}
                  url={URL.createObjectURL(file)}
                  deletePhoto={true}
                  onClick={() => {
                    const newFiles = [
                      ...formik.values.uploadReviewPhotos.files,
                    ];
                    newFiles.splice(index, 1);
                    formik.setFieldValue('uploadReviewPhotos', {
                      photos: [],
                      files: newFiles,
                    });
                    onReviewFilesChange?.(
                      newFiles,
                      formik.values.reviewVideoFiles
                    );
                  }}
                />
              ))}
              {request?.ticket_review_video?.map(
                ({ id, full_video_url, full_preview_video_url }) => (
                  <VideoPlayer
                    key={id}
                    url={full_video_url}
                    poster={full_preview_video_url}
                  />
                )
              )}
              {formik.values.reviewVideoFiles.map((file, index) => (
                <div
                  key={file.name + index}
                  className={styles.reviewVideoItem}
                >
                  <video
                    controls
                    className={styles.reviewVideoPreview}
                    src={videoFileUrls[index]}
                  />
                  <button
                    type="button"
                    className={styles.reviewVideoDelete}
                    onClick={() => {
                      const newFiles = [...formik.values.reviewVideoFiles];
                      newFiles.splice(index, 1);
                      formik.setFieldValue('reviewVideoFiles', newFiles);
                      onReviewFilesChange?.(
                        formik.values.uploadReviewPhotos.files,
                        newFiles
                      );
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>

            <div className={styles.reviewUploadButtons}>
              <div className={styles.reviewMediaUploadBtn}>
                <div className={styles.reviewMediaUploadBtnText}>
                  Загрузить фото или видео
                </div>
                <span className={styles.reviewMediaUploadBtnDesc}>
                  Фото: 1000*200 px, макс 2 мб | Видео: макс 50 мб
                </span>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="image/png,image/jpg,image/jpeg,video/*"
                  multiple
                  className={styles.reviewMediaInput}
                  onChange={e => {
                    const newFiles = e.target.files;
                    if (!newFiles) return;

                    const imageFiles: File[] = [];
                    const videoFiles: File[] = [];

                    Array.from(newFiles).forEach(file => {
                      if (file.type.startsWith('image/')) {
                        imageFiles.push(file);
                      } else if (file.type.startsWith('video/')) {
                        videoFiles.push(file);
                      }
                    });

                    if (imageFiles.length > 0) {
                      const updatedPhotoFiles = [
                        ...formik.values.uploadReviewPhotos.files,
                        ...imageFiles,
                      ];
                      formik.setFieldValue('uploadReviewPhotos', {
                        photos: [],
                        files: updatedPhotoFiles,
                      });
                    }

                    if (videoFiles.length > 0) {
                      const updatedVideoFiles = [
                        ...formik.values.reviewVideoFiles,
                        ...videoFiles,
                      ];
                      formik.setFieldValue('reviewVideoFiles', updatedVideoFiles);
                    }

                    onReviewFilesChange?.(
                      imageFiles.length > 0
                        ? [...formik.values.uploadReviewPhotos.files, ...imageFiles]
                        : formik.values.uploadReviewPhotos.files,
                      videoFiles.length > 0
                        ? [...formik.values.reviewVideoFiles, ...videoFiles]
                        : formik.values.reviewVideoFiles
                    );

                    e.target.value = '';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        </div>

        {review === formik.values.solution &&
        formik.values.uploadReviewPhotos.files.length === 0 &&
        formik.values.reviewVideoFiles.length === 0 ? (
          <Button
            type="submit"
            text={t('back_to_list')}
            className={styles.btn}
            onClick={goBack}
            disabled={false}
          />
        ) : (
          <Button
            type="button"
            text={t('save_n_exit')}
            className={styles.btn}
            onClick={handleUpdate}
            disabled={false}
          />
        )}
      </form>
    </div>
  );
};
